import type { Opportunity, Organization } from '../../types/crm'
import type {
  ProspectFieldErrors,
  ProspectFieldKey,
  ProspectOptions,
  ProspectOrganizationIndexEntry,
  ProspectSubmission,
} from './prospectForm'

export type CreatedProspect = {
  organizationCreated: boolean
  organization: Organization
  opportunity: Opportunity
}

const FIELD_ERROR_MESSAGES: Record<ProspectFieldKey, string> = {
  companyName: 'Enter a valid company name.',
  ownerName: 'Enter a valid owner name.',
  priority: 'Choose a current priority option.',
  website: 'Enter a safe http or https web address.',
  facebook: 'Enter a safe http or https web address.',
  linkedin: 'Enter a safe http or https web address.',
  email: 'Enter a valid email address.',
  phone: 'Enter a valid phone number.',
  city: 'Enter a valid city.',
  state: 'Choose a current state option.',
  source: 'Choose a current source option.',
  opportunityValue: 'Enter a valid non-negative dollar value.',
  notes: 'Enter shorter notes.',
}

const FIELD_KEYS = new Set<ProspectFieldKey>(Object.keys(FIELD_ERROR_MESSAGES) as ProspectFieldKey[])

const SAFE_ERROR_MESSAGES: Record<string, string> = {
  AUTH_REQUIRED: 'Your secure session has expired. Sign in again before adding this prospect.',
  UNAUTHENTICATED: 'Your secure session has expired. Sign in again before adding this prospect.',
  DUPLICATE: 'This company and owner already exist in the CRM.',
  DUPLICATE_PROSPECT: 'This company and owner already exist in the CRM.',
  INVALID_DROPDOWN: 'One or more CRM options changed. Return to the form and choose the current values.',
  VALIDATION_ERROR: 'Correct the highlighted fields before adding this prospect.',
  VALIDATION_FAILED: 'Correct the highlighted fields before adding this prospect.',
  WRITE_BACK_DISABLED: 'Prospect creation is not enabled on this deployment.',
  WRITE_DISABLED: 'Prospect creation is not enabled on this deployment.',
}

export class ProspectApiError extends Error {
  code: string
  fieldErrors: ProspectFieldErrors
  status: number

  constructor(message: string, code = 'PROSPECT_REQUEST_FAILED', status = 0, fieldErrors: ProspectFieldErrors = {}) {
    super(message)
    this.code = code
    this.status = status
    this.fieldErrors = fieldErrors
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

async function readJson(response: Response): Promise<Record<string, unknown>> {
  const contentType = response.headers.get('content-type') ?? ''
  if (!contentType.toLocaleLowerCase().includes('application/json')) {
    throw new ProspectApiError('The secure prospect service is unavailable.', 'INVALID_RESPONSE', response.status)
  }

  try {
    const body: unknown = await response.json()
    if (!isRecord(body)) throw new Error('Invalid response object')
    return body
  } catch (cause) {
    if (cause instanceof ProspectApiError) throw cause
    throw new ProspectApiError('The secure prospect service returned an invalid response.', 'INVALID_RESPONSE', response.status)
  }
}

function safeFieldErrors(body: Record<string, unknown>): ProspectFieldErrors {
  const source = isRecord(body.fields) ? body.fields : isRecord(body.fieldErrors) ? body.fieldErrors : null
  const errors: ProspectFieldErrors = {}
  if (source) {
    for (const key of Object.keys(source)) {
      if (FIELD_KEYS.has(key as ProspectFieldKey)) errors[key as ProspectFieldKey] = FIELD_ERROR_MESSAGES[key as ProspectFieldKey]
    }
  }
  if (Array.isArray(body.fields)) {
    for (const key of body.fields) {
      if (typeof key === 'string' && FIELD_KEYS.has(key as ProspectFieldKey)) {
        errors[key as ProspectFieldKey] = FIELD_ERROR_MESSAGES[key as ProspectFieldKey]
      }
    }
  }
  return errors
}

function responseError(response: Response, body: Record<string, unknown>): ProspectApiError {
  const code = typeof body.code === 'string' ? body.code : 'PROSPECT_REQUEST_FAILED'
  const statusMessage = response.status === 401
    ? SAFE_ERROR_MESSAGES.UNAUTHENTICATED
    : response.status === 409
      ? SAFE_ERROR_MESSAGES.DUPLICATE_PROSPECT
      : response.status === 503
        ? SAFE_ERROR_MESSAGES.WRITE_DISABLED
        : 'The prospect could not be added. Nothing was written. Try again.'
  return new ProspectApiError(SAFE_ERROR_MESSAGES[code] ?? statusMessage, code, response.status, safeFieldErrors(body))
}

function stringArray(record: Record<string, unknown>, key: keyof ProspectOptions): string[] {
  const value = record[key]
  if (!Array.isArray(value)) return []
  return value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim()))
}

function organizationIndex(body: Record<string, unknown>): ProspectOrganizationIndexEntry[] | undefined {
  if (!Object.prototype.hasOwnProperty.call(body, 'organizations')) return undefined
  if (!Array.isArray(body.organizations)) {
    throw new ProspectApiError('The live organization index returned an invalid response.', 'INVALID_RESPONSE')
  }
  const organizations: ProspectOrganizationIndexEntry[] = []
  for (const item of body.organizations) {
    if (!isRecord(item) || typeof item.name !== 'string' || !Number.isInteger(item.sheetRow) || (item.sheetRow as number) < 5) {
      throw new ProspectApiError('The live organization index returned an invalid response.', 'INVALID_RESPONSE')
    }
    organizations.push({ name: item.name, sheetRow: item.sheetRow as number })
  }
  return organizations
}

export async function getProspectOptions(signal?: AbortSignal): Promise<ProspectOptions> {
  let response: Response
  try {
    response = await fetch('/api/prospects/options', {
      credentials: 'include',
      headers: { accept: 'application/json' },
      signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ProspectApiError('The live CRM options could not be loaded. Try again.', 'OPTIONS_UNAVAILABLE')
  }

  const body = await readJson(response)
  if (!response.ok) throw responseError(response, body)
  const source = isRecord(body.options) ? body.options : body
  const options: ProspectOptions = {
    writeEnabled: body.writeEnabled === true || source.writeEnabled === true,
    organizations: organizationIndex(body),
    priority: stringArray(source, 'priority'),
    type: stringArray(source, 'type'),
    accountManager: stringArray(source, 'accountManager'),
    stage: stringArray(source, 'stage'),
    lossReason: stringArray(source, 'lossReason'),
    source: stringArray(source, 'source'),
    status: stringArray(source, 'status'),
    state: stringArray(source, 'state'),
  }

  if (options.priority.length === 0 || options.source.length === 0) {
    throw new ProspectApiError('The live CRM options are incomplete. Nothing can be submitted yet.', 'OPTIONS_INCOMPLETE')
  }
  return options
}

function isOrganization(value: unknown): value is Organization {
  if (!isRecord(value)) return false
  return typeof value.id === 'string'
    && Number.isInteger(value.sheetRow)
    && ['priority', 'name', 'type', 'accountManager', 'website', 'facebook', 'linkedin', 'email', 'phone', 'city', 'state', 'notes']
      .every((key) => typeof value[key] === 'string')
}

function isOpportunity(value: unknown): value is Opportunity {
  if (!isRecord(value)) return false
  return typeof value.id === 'string'
    && Number.isInteger(value.sheetRow)
    && ['priority', 'name', 'dealOwner', 'organization', 'status', 'stage', 'lossReason', 'source', 'expectedClose', 'notes']
      .every((key) => typeof value[key] === 'string')
    && typeof value.value === 'number'
    && typeof value.probability === 'number'
    && (value.dateCollected === undefined || typeof value.dateCollected === 'string')
}

export async function createProspect(submission: ProspectSubmission, signal?: AbortSignal): Promise<CreatedProspect> {
  let response: Response
  try {
    response = await fetch('/api/prospects', {
      method: 'POST',
      credentials: 'include',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(submission),
      signal,
    })
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new ProspectApiError('The prospect could not be added. Nothing was written. Check your connection and try again.')
  }

  const body = await readJson(response)
  if (!response.ok) throw responseError(response, body)
  if (!isRecord(body.prospect)
    || typeof body.prospect.organizationCreated !== 'boolean'
    || !isOrganization(body.prospect.organization)
    || !isOpportunity(body.prospect.opportunity)) {
    throw new ProspectApiError('The prospect was accepted, but the secure service returned an invalid record.', 'INVALID_RESPONSE', response.status)
  }

  return {
    organizationCreated: body.prospect.organizationCreated,
    organization: body.prospect.organization,
    opportunity: body.prospect.opportunity,
  }
}
